import { getMediaData } from './media.utils'
import type { Media } from '@/types'

export interface AbstractContent {
  root: AbstractNode
  [k: string]: unknown
}

interface AbstractNode {
  type: string
  text?: string
  children?: AbstractNode[]
  format?: string | number
  style?: string
  mode?: string
  direction?: ('ltr' | 'rtl') | null
  indent?: number
  version?: number
  tag?: string
  listType?: string
  start?: number
  url?: string
  target?: string
  rel?: string
  value?: Media | number
  attributes?: Record<string, string>
  [k: string]: unknown
}

/**
 * Lexical text format bitmask:
 * 1 = bold, 2 = italic, 4 = strikethrough, 8 = underline, 16 = code, 32 = subscript, 64 = superscript
 */
function applyTextFormat(text: string, format: number): string {
  if (!format) return text

  let result = text
  if (format & 16) result = `<code>${result}</code>`
  if (format & 1) result = `<strong>${result}</strong>`
  if (format & 2) result = `<em>${result}</em>`
  if (format & 4) result = `<s>${result}</s>`
  if (format & 8) result = `<u>${result}</u>`
  if (format & 32) result = `<sub>${result}</sub>`
  if (format & 64) result = `<sup>${result}</sup>`

  return result
}

function processNode(node: AbstractNode): string {
  const children = node.children?.map(processNode).join('') || ''

  switch (node.type) {
    case 'text':
      const text = node.text || ''
      return typeof node.format === 'number' ? applyTextFormat(text, node.format) : text

    case 'linebreak':
      return '<br />'

    case 'paragraph':
      return children ? `<p>${children}</p>` : '<p><br /></p>'

    case 'heading':
      const headingTag = node.tag || 'h1'
      return `<${headingTag}>${children}</${headingTag}>`

    case 'quote':
      return `<blockquote>${children}</blockquote>`

    case 'list':
      const listTag = node.listType === 'number' ? 'ol' : 'ul'
      const startAttr = node.start && node.start !== 1 ? ` start="${node.start}"` : ''
      return `<${listTag}${startAttr}>${children}</${listTag}>`

    case 'listitem':
      return `<li>${children}</li>`

    case 'link':
      const href = node.url || '#'
      const target = node.target ? ` target="${node.target}"` : ''
      const rel = node.rel ? ` rel="${node.rel}"` : ''
      return `<a href="${href}"${target}${rel}>${children}</a>`

    case 'horizontalrule':
      return '<hr />'

    case 'code':
      return `<pre><code>${children}</code></pre>`

    case 'upload':
      const mediaData = getMediaData(node.value)
      if (!mediaData.url) return ''
      return `<img src="${mediaData.url}" alt="${mediaData.alt || ''}" />`

    case 'image':
      return `<img src="${node.url || ''}" alt="${node.attributes?.alt || ''}" />`

    case 'root':
      return children

    default:
      // Fallback: tag varsa element olarak işle
      if (node.tag) {
        return `<${node.tag}>${children}</${node.tag}>`
      }
      return children
  }
}

export function lexicalStateToHtml(state: AbstractContent | null | undefined): string {
  if (!state?.root) return ''
  return processNode(state.root)
}

export function lexicalStateToPlainText(state: AbstractContent | null | undefined): string {
  if (!state?.root) return ''

  function extractText(node: AbstractNode): string {
    if (node.type === 'text') return node.text || ''
    if (node.type === 'linebreak') return '\n'
    if (node.type === 'paragraph') {
      const text = node.children?.map(extractText).join('') || ''
      return text + '\n'
    }
    if (node.type === 'heading') {
      const text = node.children?.map(extractText).join('') || ''
      return text + '\n'
    }
    if (node.type === 'listitem') {
      return '• ' + (node.children?.map(extractText).join('') || '') + '\n'
    }
    if (node.type === 'horizontalrule') return '\n---\n'

    return node.children?.map(extractText).join('') || ''
  }

  return extractText(state.root).trim()
}

/**
 * Check if rich text content has actual text content
 */
export function hasRichTextContent(state: AbstractContent | null | undefined): boolean {
  return lexicalStateToPlainText(state).length > 0
}
